import React from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  Avatar,
  Chip,
  Paper
} from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import logo from '../assets/dd-hrms-logo.png';

const drawerWidth = 260;

const MainLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (!user) return <Outlet />;

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) {
      return user.roles.includes('ROLE_OWNER')
        ? 'Organization Overview'
        : 'My Workspace';
    }
    if (path.includes('approvals')) return 'Approval Center';
    if (path.includes('reports')) return 'Consolidated Insights';
    if (path.includes('daily-pay')) return 'My Daily Pay';
    return 'DD-HRMS Portal';
  };

  const roleLabel =
    user.roles?.[0]?.replace('ROLE_', '') || 'USER';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      {/* ================= SIDEBAR ================= */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'rgba(178, 248, 233, 0.32)',
            backdropFilter: 'blur(14px)',
            borderRight: '1px solid rgba(0,0,0,0.06)'
          }
        }}
      >
        {/* LOGO AREA */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
            gap: 1
          }}
        >
          <img src={logo} alt="DD-HRMS" style={{ height: 40 }} />
          <Typography
            variant="h5"
            fontWeight={900}
            sx={{
              background:
                'linear-gradient(90deg,#1976d2,#42a5f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            DD-HRMS
          </Typography>
        </Box>

        <Sidebar />
      </Drawer>

      {/* ================= MAIN AREA ================= */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        
        {/* ================= TOP BAR ================= */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            ml: `${drawerWidth}px`,
            width: `calc(100% - ${drawerWidth}px)`,
            background: 'rgba(117, 235, 139, 0.7)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(133, 73, 16, 0.83)'
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              variant="h6"
              fontWeight={800}
              color="#0F172A"
            >
              {getPageTitle()}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={roleLabel}
                size="small"
                color="#0F172A"
                sx={{ fontWeight: 700 }}
              />

              <Typography fontSize={14} fontWeight={600} color="#0F172A">
                {user.username}
              </Typography>

              <Avatar
                sx={{
                  bgcolor: '#0F172A',
                  width: 36,
                  height: 36,
                  fontWeight: 700
                }}
              >
                {user.username?.charAt(0).toUpperCase()}
              </Avatar>

              <Button
                onClick={logout}
                color="error"
                size="small"
                sx={{ fontWeight: 700 }}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* ================= CONTENT ================= */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: 8,
            p: 3,
            background:
              'rgba(132, 182, 248, 0.18)'
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              minHeight: 'calc(100vh - 140px)',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(145, 81, 81, 0.06)'
            }}
          >
            <Outlet />
          </Paper>
        </Box>

        {/* ================= FOOTER ================= */}
        <Box
          sx={{
            textAlign: 'center',
            py: 1.5,
            borderTop: '1px solid rgba(161, 61, 61, 0.06)',
            background: 'rgba(255, 255, 255, 0.6)'
          }}
        >
          <Typography variant="caption" color="#0F172A">
            © {new Date().getFullYear()} <b>DD-HRMS</b>. All rights reserved.
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

export default MainLayout;
